import React, { useState, useEffect } from "react";
import "./PianificazioneFinanziaria.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { format, addDays, isSameDay, startOfDay } from "date-fns";
import { it } from "date-fns/locale";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import EditIcon from "../../assets/edit_icon.svg";
import DeleteIcon from "../../assets/trash_icon_w.svg";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

export default function PianificazioneFinanziaria() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);
  const { addDocument, updateDocument } = useFirestore("saldi_bancari");
  const {
    addDocument: addMovimento,
    updateDocument: updateMovimento,
    deleteDocument,
  } = useFirestore("movimenti_finanziari");
  const { documents: saldi } = useCollection("saldi_bancari", null, [
    "createdAt",
    "desc",
  ]);
  const { documents: movimenti } = useCollection("movimenti_finanziari", null, [
    "data",
    "asc",
  ]);

  // Stati per il saldo bancario
  const [saldoAttuale, setSaldoAttuale] = useState(0);
  const [nuovoSaldo, setNuovoSaldo] = useState("");
  const [dataAggiornamento, setDataAggiornamento] = useState(new Date());
  const [noteSaldo, setNoteSaldo] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Stati per i movimenti
  const [tipoMovimento, setTipoMovimento] = useState("costo"); // costo o ricavo
  const [descrizioneMovimento, setDescrizioneMovimento] = useState("");
  const [importoMovimento, setImportoMovimento] = useState("");
  const [dataMovimento, setDataMovimento] = useState(new Date());
  const [categoriaMovimento, setCategoriaMovimento] = useState("");
  const [noteMovimento, setNoteMovimento] = useState("");
  const [movimentoId, setMovimentoId] = useState(null); // ID del movimento in fase di modifica
  const [isEditingMovimento, setIsEditingMovimento] = useState(false); // Flag per indicare se stiamo modificando un movimento

  // Stato per la data di previsione
  const [dataPrevisione, setDataPrevisione] = useState(new Date());
  const [saldoPrevisto, setSaldoPrevisto] = useState(0);

  // Stato per il grafico
  const [giorniGrafico, setGiorniGrafico] = useState(30);
  const [chartData, setChartData] = useState([]);

  // Stati per i toggle delle sezioni
  const [showSaldoSection, setShowSaldoSection] = useState(false);
  const [showMovimentiSection, setShowMovimentiSection] = useState(false);

  // Categorie di movimenti
  const categorieCosti = [
    "Stipendi",
    "Fornitori",
    "Affitto",
    "Utenze",
    "Tasse",
    "Manutenzione",
    "Investimenti",
    "Altro",
  ];

  const categorieRicavi = [
    "Vendite",
    "Servizi",
    "Consulenze",
    "Rimborsi",
    "Investimenti",
    "Altro",
  ];

  // Carica il saldo attuale dal database
  useEffect(() => {
    if (saldi && saldi.length > 0) {
      setSaldoAttuale(saldi[0].importo);
    }
  }, [saldi]);

  // Calcola i dati per il grafico
  useEffect(() => {
    if (!saldi || saldi.length === 0 || !movimenti) return;

    const saldoBase = saldi[0].importo;
    const today = startOfDay(new Date());

    // Calcola il saldo di partenza (Oggi)
    // Applica tutti i movimenti dalla data di aggiornamento del saldo fino a oggi (incluso)
    let currentBalance = saldoBase;

    // Ordina i movimenti per data
    const sortedMovimenti = [...movimenti].sort(
      (a, b) => a.data.toDate() - b.data.toDate()
    );

    // Prima, allineiamo il saldo ad oggi
    sortedMovimenti.forEach((movimento) => {
      const dataMov = startOfDay(movimento.data.toDate());

      // Salta movimenti futuri rispetto a oggi (li tratteremo nel loop dei giorni)
      if (dataMov > today) return;

      if (movimento.isInsoluto || movimento.excludedFromCalculation) return;

      if (movimento.tipo === "ricavo") {
        currentBalance += parseFloat(movimento.importo);
      } else {
        currentBalance -= parseFloat(movimento.importo);
      }
    });

    const data = [];

    // Genera i dati giorno per giorno da Oggi in poi
    let balanceRunner = currentBalance;

    // Aggiungi il punto iniziale (Oggi)
    data.push({
      data: format(today, "dd/MM"),
      fullDate: today,
      saldo: balanceRunner,
    });

    for (let i = 1; i <= giorniGrafico; i++) {
      const targetDate = addDays(today, i);

      // Trova i movimenti di questo giorno
      const dailyMovimenti = sortedMovimenti.filter((m) => {
        const mDate = startOfDay(m.data.toDate());
        return (
          isSameDay(mDate, targetDate) &&
          !m.isInsoluto &&
          !m.excludedFromCalculation
        );
      });

      // Applica i movimenti
      for (const m of dailyMovimenti) {
        if (m.tipo === "ricavo") {
          balanceRunner += parseFloat(m.importo);
        } else {
          balanceRunner -= parseFloat(m.importo);
        }
      }

      data.push({
        data: format(targetDate, "dd/MM"),
        fullDate: targetDate,
        saldo: balanceRunner,
      });
    }

    setChartData(data);
  }, [saldi, movimenti, giorniGrafico]);

  // Calcola il saldo previsto in base alla data di previsione
  useEffect(() => {
    if (!saldi || saldi.length === 0 || !movimenti) return;

    const saldoBase = saldi[0].importo;
    let saldoCalcolato = saldoBase;

    // Filtra i movimenti fino alla data di previsione
    const movimentiFiltered = movimenti.filter((movimento) => {
      const dataMovimento = startOfDay(movimento.data.toDate());
      const dataPrev = startOfDay(dataPrevisione);

      // Escludi insoluti
      if (movimento.isInsoluto) return false;
      // Escludi movimenti disabilitati dal calcolo
      if (movimento.excludedFromCalculation) return false;

      return dataMovimento <= dataPrev;
    });

    // Calcola il saldo previsto
    console.group("Calcolo Saldo Previsto");
    console.log("Saldo Base (iniziale):", saldoBase);
    console.log("Totale Movimenti Filtrati:", movimentiFiltered.length);
    let debugSum = 0;

    movimentiFiltered.forEach((movimento) => {
      const val = parseFloat(movimento.importo);
      if (movimento.tipo === "ricavo") {
        saldoCalcolato += val;
        debugSum += val;
      } else {
        saldoCalcolato -= val;
        debugSum -= val;
      }
    });
    console.log("Somma algebrica movimenti:", debugSum);
    console.log("Saldo Calcolato Finale:", saldoCalcolato);
    console.groupEnd();

    setSaldoPrevisto(saldoCalcolato);
  }, [saldi, movimenti, dataPrevisione]);

  // Gestione flag esclusione dal calcolo
  const toggleExclusion = async (movimento) => {
    try {
      await updateMovimento(movimento.id, {
        excludedFromCalculation: !movimento.excludedFromCalculation,
      });
      toast.success(
        movimento.excludedFromCalculation
          ? "Movimento reincluso nel calcolo"
          : "Movimento escluso dal calcolo"
      );
    } catch (error) {
      toast.error("Errore durante l'aggiornamento: " + error.message);
    }
  };

  // Gestione spostamento in insoluti
  const toggleInsoluto = async (movimento) => {
    try {
      const newState = !movimento.isInsoluto;
      await updateMovimento(movimento.id, {
        isInsoluto: newState,
        // Se diventa insoluto, lo escludiamo anche dal calcolo automaticamente?
        // La logica di calcolo già esclude gli insoluti, quindi non serve settare excludedFromCalculation esplicitamente,
        // ma per pulizia lasciamo che sia il flag isInsoluto a guidare.
      });
      toast.success(
        newState
          ? "Movimento spostato in Insoluti"
          : "Movimento ripristinato da Insoluti"
      );
    } catch (error) {
      toast.error("Errore durante l'aggiornamento: " + error.message);
    }
  };

  // Aggiorna il saldo bancario
  const handleUpdateSaldo = async (e) => {
    e.preventDefault();

    if (!nuovoSaldo || isNaN(parseFloat(nuovoSaldo))) {
      toast.error("Inserisci un importo valido");
      return;
    }

    setIsSaving(true);

    try {
      // Se esiste già un saldo, aggiornalo
      if (saldi && saldi.length > 0) {
        await updateDocument(saldi[0].id, {
          importo: parseFloat(nuovoSaldo),
          dataAggiornamento: dataAggiornamento,
          note: noteSaldo,
          updatedAt: new Date(),
        });
      } else {
        // Altrimenti crea un nuovo record
        await addDocument({
          importo: parseFloat(nuovoSaldo),
          dataAggiornamento: dataAggiornamento,
          note: noteSaldo,
          createdAt: new Date(),
        });
      }

      toast.success("Saldo aggiornato con successo");
      setNuovoSaldo("");
      setNoteSaldo("");
    } catch (error) {
      toast.error("Errore durante l'aggiornamento del saldo: " + error.message);
    }

    setIsSaving(false);
  };

  // Aggiungi o modifica un movimento
  const handleAddMovimento = async (e) => {
    e.preventDefault();

    if (!descrizioneMovimento) {
      toast.error("Inserisci una descrizione per il movimento");
      return;
    }

    if (!importoMovimento || isNaN(parseFloat(importoMovimento))) {
      toast.error("Inserisci un importo valido");
      return;
    }

    if (!categoriaMovimento) {
      toast.error("Seleziona una categoria");
      return;
    }

    setIsSaving(true);

    const movimentoData = {
      tipo: tipoMovimento,
      descrizione: descrizioneMovimento,
      importo: parseFloat(importoMovimento),
      data: dataMovimento,
      categoria: categoriaMovimento,
      note: noteMovimento,
      updatedAt: new Date(),
    };

    try {
      if (isEditingMovimento && movimentoId) {
        // Aggiorna un movimento esistente
        await updateMovimento(movimentoId, movimentoData);
        toast.success("Movimento aggiornato con successo");
      } else {
        // Aggiungi un nuovo movimento
        await addMovimento({
          ...movimentoData,
          createdAt: new Date(),
          createdBy: user.uid,
        });
        toast.success("Movimento aggiunto con successo");
      }

      // Reset del form
      resetMovimentoForm();
    } catch (error) {
      toast.error(
        `Errore durante ${
          isEditingMovimento ? "l'aggiornamento" : "l'aggiunta"
        } del movimento: ${error.message}`
      );
    }

    setIsSaving(false);
  };

  // Reset del form dei movimenti
  const resetMovimentoForm = () => {
    setTipoMovimento("costo");
    setDescrizioneMovimento("");
    setImportoMovimento("");
    setDataMovimento(new Date());
    setCategoriaMovimento("");
    setNoteMovimento("");
    setMovimentoId(null);
    setIsEditingMovimento(false);
  };

  // Carica un movimento per la modifica
  const handleEditMovimento = (movimento) => {
    setMovimentoId(movimento.id);
    setTipoMovimento(movimento.tipo);
    setDescrizioneMovimento(movimento.descrizione);
    setImportoMovimento(movimento.importo.toString());
    setDataMovimento(movimento.data.toDate());
    setCategoriaMovimento(movimento.categoria);
    setNoteMovimento(movimento.note || "");
    setIsEditingMovimento(true);
    setShowMovimentiSection(true); // Apri la sezione movimenti

    // Scorri fino alla sezione movimenti
    const sezioneMovimenti = document.querySelector(".sezione-movimenti");
    if (sezioneMovimenti) {
      sezioneMovimenti.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Elimina un movimento
  const handleDeleteMovimento = async (id) => {
    if (window.confirm("Sei sicuro di voler eliminare questo movimento?")) {
      try {
        await deleteDocument(id);
        toast.success("Movimento eliminato con successo");
      } catch (error) {
        toast.error(
          "Errore durante l'eliminazione del movimento: " + error.message
        );
      }
    }
  };

  // Formatta la valuta
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  return (
    <div className="pianificazione-finanziaria-container">
      <h1>Pianificazione Finanziaria</h1>

      {admin && (
        <div className="pianificazione-content">
          {/* Sezione Previsione - Ora è la prima sezione */}
          <div className="sezione-previsione">
            <h2>Previsione Saldo</h2>
            <div className="saldo-display">
              <h3>{formatCurrency(saldoAttuale)}</h3>
              {saldi && saldi.length > 0 && (
                <p className="ultimo-aggiornamento">
                  Ultimo aggiornamento:{" "}
                  {format(saldi[0].dataAggiornamento.toDate(), "dd MMMM yyyy", {
                    locale: it,
                  })}
                </p>
              )}
            </div>
            <div className="form-group">
              <label>Data di previsione:</label>
              <div className="date-selection-container">
                <DatePicker
                  selected={dataPrevisione}
                  onChange={(date) => setDataPrevisione(date)}
                  dateFormat="dd/MM/yyyy"
                  className="date-picker"
                />
                <div className="date-buttons-container">
                  <button
                    type="button"
                    className="btn-date"
                    onClick={() => setDataPrevisione(new Date())}
                  >
                    Oggi
                  </button>
                  <button
                    type="button"
                    className="btn-date"
                    onClick={() => {
                      const newDate = new Date(dataPrevisione);
                      newDate.setDate(newDate.getDate() + 5);
                      setDataPrevisione(newDate);
                    }}
                  >
                    +5 giorni
                  </button>
                  <button
                    type="button"
                    className="btn-date"
                    onClick={() => {
                      const newDate = new Date(dataPrevisione);
                      newDate.setDate(newDate.getDate() + 10);
                      setDataPrevisione(newDate);
                    }}
                  >
                    +10 giorni
                  </button>
                  <button
                    type="button"
                    className="btn-date"
                    onClick={() => {
                      const newDate = new Date(dataPrevisione);
                      newDate.setDate(newDate.getDate() + 15);
                      setDataPrevisione(newDate);
                    }}
                  >
                    +15 giorni
                  </button>
                </div>
              </div>
            </div>
            <div className="saldo-previsione">
              <h3>
                Saldo previsto al{" "}
                {format(dataPrevisione, "dd MMMM yyyy", { locale: it })}:
              </h3>
              <div
                className={`importo-previsione ${
                  saldoPrevisto < 0 ? "negativo" : "positivo"
                }`}
              >
                {formatCurrency(saldoPrevisto)}
              </div>
            </div>

            {/* Lista movimenti fino alla data di previsione */}
            {movimenti && movimenti.length > 0 && (
              <>
                <div className="lista-movimenti">
                  <h3>Movimenti inclusi nella previsione</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descrizione</th>
                        <th>Categoria</th>
                        <th>Importo</th>
                        <th>Stato</th>
                        <th>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimenti
                        .filter((movimento) => {
                          const movDate = startOfDay(movimento.data.toDate());
                          const targetDate = startOfDay(dataPrevisione);
                          return movDate <= targetDate && !movimento.isInsoluto;
                        })
                        .sort((a, b) => a.data.toDate() - b.data.toDate())
                        .map((movimento) => (
                          <tr
                            key={movimento.id}
                            className={`${movimento.tipo} ${
                              movimento.excludedFromCalculation
                                ? "escluso-calcolo"
                                : ""
                            }`}
                          >
                            <td>
                              {format(movimento.data.toDate(), "dd/MM/yyyy")}
                            </td>
                            <td>{movimento.descrizione}</td>
                            <td>{movimento.categoria}</td>
                            <td className="importo">
                              {movimento.tipo === "ricavo" ? "+" : "-"}
                              {formatCurrency(movimento.importo)}
                            </td>
                            <td className="stato-movimento">
                              {movimento.excludedFromCalculation && (
                                <span className="badge escluso">Escluso</span>
                              )}
                            </td>
                            <td className="col-azioni">
                              <button
                                className="btn-icon-action btn-toggle"
                                title={
                                  movimento.excludedFromCalculation
                                    ? "Includi nel calcolo"
                                    : "Escludi dal calcolo"
                                }
                                onClick={() => toggleExclusion(movimento)}
                              >
                                <span className="emoji-icon">
                                  {movimento.excludedFromCalculation
                                    ? "➕"
                                    : "🚫"}
                                </span>
                              </button>
                              <button
                                className="btn-icon-action btn-insoluto"
                                title="Segna come insoluto"
                                onClick={() => toggleInsoluto(movimento)}
                              >
                                <span className="emoji-icon">⚠️</span>
                              </button>
                              <button
                                className="btn-icon-action btn-edit"
                                title="Modifica"
                                onClick={() => handleEditMovimento(movimento)}
                              >
                                <img src={EditIcon} alt="Edit Icon" />
                              </button>
                              <button
                                className="btn-icon-action btn-delete"
                                title="Elimina"
                                onClick={() =>
                                  handleDeleteMovimento(movimento.id)
                                }
                              >
                                <img src={DeleteIcon} alt="Delete Icon" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {/* Tabella Insoluti */}
                <div className="lista-movimenti lista-insoluti">
                  <h3 className="insoluti-header">
                    ⚠️ Insoluti - Da Sollecitare
                  </h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descrizione</th>
                        <th>Categoria</th>
                        <th>Importo</th>
                        <th>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimenti
                        .filter((movimento) => movimento.isInsoluto)
                        .sort((a, b) => a.data.toDate() - b.data.toDate())
                        .map((movimento) => (
                          <tr key={movimento.id} className="insoluto-row">
                            <td>
                              {format(movimento.data.toDate(), "dd/MM/yyyy")}
                            </td>
                            <td>{movimento.descrizione}</td>
                            <td>{movimento.categoria}</td>
                            <td className="importo">
                              {movimento.tipo === "ricavo" ? "+" : "-"}
                              {formatCurrency(movimento.importo)}
                            </td>
                            <td className="col-azioni">
                              <button
                                className="btn-icon-action btn-restore"
                                title="Ripristina nei movimenti"
                                onClick={() => toggleInsoluto(movimento)}
                              >
                                <span className="emoji-icon">🔙</span>
                              </button>
                              <button
                                className="btn-icon-action btn-edit"
                                title="Modifica"
                                onClick={() => handleEditMovimento(movimento)}
                              >
                                <img src={EditIcon} alt="Edit Icon" />
                              </button>
                              <button
                                className="btn-icon-action btn-delete"
                                title="Elimina"
                                onClick={() =>
                                  handleDeleteMovimento(movimento.id)
                                }
                              >
                                <img src={DeleteIcon} alt="Delete Icon" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      {movimenti.filter((m) => m.isInsoluto).length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center">
                            Nessun insoluto presente
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Grafico Andamento Saldo */}
                <div className="sezione-grafico">
                  <h3>Andamento Saldo Previsto</h3>

                  <div className="grafico-controlli">
                    <span>Periodo di monitoraggio: </span>
                    {[5, 10, 15, 30, 60, 90].map((giorni) => (
                      <button
                        key={giorni}
                        className={`btn-grafico ${
                          giorniGrafico === giorni ? "active" : ""
                        }`}
                        onClick={() => setGiorniGrafico(giorni)}
                      >
                        {giorni} gg
                      </button>
                    ))}
                  </div>

                  <div
                    className="chart-container"
                    style={{ width: "100%", height: 400, marginTop: "20px" }}
                  >
                    <ResponsiveContainer>
                      <LineChart
                        data={chartData}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="data" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          labelFormatter={(label) => `Data: ${label}`}
                        />
                        <Legend />
                        <ReferenceLine
                          y={200000}
                          label="Soglia 200k"
                          stroke="red"
                          strokeDasharray="3 3"
                        />
                        <Line
                          type="monotone"
                          dataKey="saldo"
                          name="Saldo Previsto"
                          stroke="#8884d8"
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Sezione Saldo Bancario - Ora è la seconda sezione con toggle */}
          <div className="sezione-saldo">
            <h2
              className="toggle-header"
              onClick={() => setShowSaldoSection(!showSaldoSection)}
            >
              Aggiorna Saldo Bancario {showSaldoSection ? "▼" : "►"}
            </h2>
            {showSaldoSection && (
              <form className="form-saldo" onSubmit={handleUpdateSaldo}>
                <div className="form-group">
                  <label>Nuovo Saldo:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={nuovoSaldo}
                    onChange={(e) => setNuovoSaldo(e.target.value)}
                    placeholder="Inserisci il nuovo saldo"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Data Aggiornamento:</label>
                  <DatePicker
                    selected={dataAggiornamento}
                    onChange={(date) => setDataAggiornamento(date)}
                    dateFormat="dd/MM/yyyy"
                    className="date-picker"
                  />
                </div>
                <div className="form-group">
                  <label>Note:</label>
                  <textarea
                    value={noteSaldo}
                    onChange={(e) => setNoteSaldo(e.target.value)}
                    placeholder="Note opzionali"
                  />
                </div>
                <button className="btn-salva" disabled={isSaving}>
                  {isSaving ? "Salvataggio..." : "Aggiorna Saldo"}
                </button>
              </form>
            )}
          </div>

          {/* Sezione Movimenti - Ora è la terza sezione con toggle */}
          <div className="sezione-movimenti">
            <h2
              className="toggle-header"
              onClick={() => setShowMovimentiSection(!showMovimentiSection)}
            >
              Gestione Movimenti {showMovimentiSection ? "▼" : "►"}
            </h2>

            {showMovimentiSection && (
              <form className="form-movimento" onSubmit={handleAddMovimento}>
                <div className="form-group">
                  <label>Tipo:</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        value="costo"
                        checked={tipoMovimento === "costo"}
                        onChange={() => setTipoMovimento("costo")}
                      />
                      Costo
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="ricavo"
                        checked={tipoMovimento === "ricavo"}
                        onChange={() => setTipoMovimento("ricavo")}
                      />
                      Ricavo
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>Descrizione:</label>
                  <input
                    type="text"
                    value={descrizioneMovimento}
                    onChange={(e) => setDescrizioneMovimento(e.target.value)}
                    placeholder="Descrizione del movimento"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Importo:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={importoMovimento}
                    onChange={(e) => setImportoMovimento(e.target.value)}
                    placeholder="Importo"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Data:</label>
                  <DatePicker
                    selected={dataMovimento}
                    onChange={(date) => setDataMovimento(date)}
                    dateFormat="dd/MM/yyyy"
                    className="date-picker"
                  />
                </div>
                <div className="form-group">
                  <label>Categoria:</label>
                  <select
                    value={categoriaMovimento}
                    onChange={(e) => setCategoriaMovimento(e.target.value)}
                    required
                  >
                    <option value="">Seleziona categoria</option>
                    {tipoMovimento === "costo"
                      ? categorieCosti.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))
                      : categorieRicavi.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Note:</label>
                  <textarea
                    value={noteMovimento}
                    onChange={(e) => setNoteMovimento(e.target.value)}
                    placeholder="Note opzionali"
                  />
                </div>
                <div className="form-buttons">
                  <button className="btn-salva" disabled={isSaving}>
                    {isSaving
                      ? "Salvataggio..."
                      : isEditingMovimento
                      ? "Aggiorna Movimento"
                      : "Aggiungi Movimento"}
                  </button>
                  {isEditingMovimento && (
                    <button
                      type="button"
                      className="btn-annulla"
                      onClick={resetMovimentoForm}
                      disabled={isSaving}
                    >
                      Annulla
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
